import { Component, Injector, OnInit, ViewChild } from '@angular/core';
import { Validators } from '@angular/forms';
import { BaseCrudComponent } from 'projects/ProjetoBaseAngular/app/base-crud.component';
import { TypesService } from 'projects/admin/src/domain/services';
import { MetaMsgTemplateComponentModel, MetaMsgTemplateModel } from '../../domain/models';
import { MetaMsgTemplateService } from '../../domain/services/meta-msg-template.service';
import { MetaMsgTemplateMetaListModalComponent } from './msg-template-list-modal/msg-template-list-modal.component';
import { MetaMsgTemplateModalComponent } from './msg-template-modal/msg-template-modal.component';
import { MetaMsgTemplateComponentType, MetaMsgTemplateStatus } from '../../domain/types';
import Swal from 'sweetalert2';
import { ModalDirective } from 'ngx-bootstrap/modal';

interface MetaMsgTemplateSendParametro {
  key: string;
  label: string;
  exemplo: string;
}

@Component({
  selector: 'app-meta-msg-template',
  templateUrl: './meta-msg-template.component.html'
})
export class MetaMsgTemplateComponent extends BaseCrudComponent<MetaMsgTemplateModel> implements OnInit {
  @ViewChild('modal') modal: MetaMsgTemplateModalComponent;
  @ViewChild('metaListModal') metaListModal: MetaMsgTemplateMetaListModalComponent;
  @ViewChild('sendTestModal') sendTestModal: ModalDirective;

  sendTemplate: MetaMsgTemplateModel;
  sendParametros: MetaMsgTemplateSendParametro[] = [];
  sendPreviewComponents: MetaMsgTemplateComponentModel[] = [];
  sendingTest = false;

  selectColumns = [
    { data: 'name' },
    { data: 'language' },
    { data: 'categoria' },
    { data: 'status' },
    { data: 'qualityScore' },
    { data: 'submittedAt' },
    { data: 'metaUpdatedAt' }
  ];

  constructor(
    injector: Injector,
    readonly service: MetaMsgTemplateService,
    readonly typesService: TypesService
  ) {
    super(injector, service);
  }

  ngOnInit() {
    super.ngOnInit();
    this.dtOptions.order = [[1, 'asc']];
    this.initializeSendForm();
  }

  importMetaTemplates() {
    if (!this.isAllowed('Create') && !this.isAllowed('Update')) {
      Swal.fire('Você não tem permissão para sincronizar templates neste módulo.', '', 'warning');
      return;
    }

    this.commonService.mensagemConfirmacao(
      'Sincronizar templates da Meta?',
      'Templates já existentes serão atualizados pelo ID da Meta ou por nome e idioma.',
      'question'
    ).then(result => {
      if (!result) {
        return;
      }

      this.isBusy = true;
      this.service.importMetaTemplates().subscribe(
        () => {
          this.isBusy = false;
          this.filter();
          this.snackBar.open('Templates sincronizados com a Meta.', 'Ok', { duration: 3000 });
        },
        errors => {
          this.isBusy = false;
          Swal.fire('Erro!', errors.join('<br/>'), 'error');
        }
      );
    });
  }

  viewMetaTemplates() {
    if (!this.isAllowed('Read')) {
      Swal.fire('Você não tem permissão de leitura neste módulo.', '', 'warning');
      return;
    }

    this.isBusy = true;
    this.service.getMetaTemplates().subscribe(
      templates => {
        this.isBusy = false;
        this.metaListModal.show(templates);
      },
      errors => {
        this.isBusy = false;
        Swal.fire('Erro!', errors.join('<br/>'), 'error');
      }
    );
  }

  publishToMeta(model: MetaMsgTemplateModel) {
    if (!this.isAllowed('Update')) {
      Swal.fire('Você não tem permissão de atualização neste módulo.', '', 'warning');
      return;
    }

    this.commonService.mensagemConfirmacao(
      'Enviar template para revisão da Meta?',
      'Após o envio, o status passa a ser controlado pela Meta.',
      'question'
    ).then(result => {
      if (!result) {
        return;
      }

      this.isBusy = true;
      this.service.publishToMeta(model.id).subscribe(
        response => {
          this.isBusy = false;
          this.applyToList(response);
          this.snackBar.open('Template enviado para a Meta.', 'Ok', { duration: 3000 });
        },
        errors => {
          this.isBusy = false;
          Swal.fire('Erro!', errors.join('<br/>'), 'error');
        }
      );
    });
  }

  canPublish(model: MetaMsgTemplateModel): boolean {
    return model.status === MetaMsgTemplateStatus.Draft || (!model.metaTemplateId && model.submittedAt !== null);
  }

  canSendTest(model: MetaMsgTemplateModel): boolean {
    return model.status === MetaMsgTemplateStatus.Approved;
  }

  openSendTest(model: MetaMsgTemplateModel) {
    if (!this.isAllowed('Update')) {
      Swal.fire('Você não tem permissão de atualização neste módulo.', '', 'warning');
      return;
    }

    this.isBusy = true;
    this.service.getSendTemplateDetails(model.id).subscribe(
      template => {
        this.isBusy = false;
        this.sendTemplate = template;
        this.sendPreviewComponents = (template.components || [])
          .filter(component => component.tipo !== MetaMsgTemplateComponentType.Buttons)
          .sort((a, b) => a.order - b.order);
        this.sendParametros = this.extractSendParametros(this.sendPreviewComponents);
        this.initializeSendForm();
        this.sendParametros.forEach(parametro => {
          this.form.addControl(
            this.getParametroControlName(parametro.key),
            this.formBuilder.control('', Validators.required)
          );
        });
        this.sendTestModal.show();
      },
      errors => {
        this.isBusy = false;
        Swal.fire('Erro!', errors.join('<br/>'), 'error');
      }
    );
  }

  cancelSendTest() {
    this.sendTestModal.hide();
  }

  sendTest() {
    if (this.form.invalid) {
      Object.keys(this.form.controls).forEach(key => this.form.get(key).markAsTouched());
      return;
    }

    const valoresParametros = this.sendParametros.reduce((result, parametro) => {
      result[parametro.key] = this.form.get(this.getParametroControlName(parametro.key)).value;
      return result;
    }, {} as { [key: string]: string });

    this.sendingTest = true;
    this.service.sendTemplate(this.sendTemplate.id, this.form.get('telefone').value, valoresParametros).subscribe(
      messageId => {
        this.sendingTest = false;
        this.sendTestModal.hide();
        this.snackBar.open(`Template enviado. ID da mensagem: ${messageId}`, 'Ok', { duration: 5000 });
      },
      errors => {
        this.sendingTest = false;
        Swal.fire('Erro!', errors.join('<br/>'), 'error');
      }
    );
  }

  getPreviewText(component: MetaMsgTemplateComponentModel): string {
    if (!component?.text) {
      return '';
    }

    return component.text.replace(/\{\{([^}]+)\}\}/g, (_match, variable) => {
      const key = `${variable}`.trim();
      const value = this.form?.get(this.getParametroControlName(key))?.value;
      return value || `{{${key}}}`;
    });
  }

  getComponentTitle(component: MetaMsgTemplateComponentModel): string {
    switch (component.tipo) {
      case MetaMsgTemplateComponentType.Header:
        return 'Cabeçalho';
      case MetaMsgTemplateComponentType.Footer:
        return 'Rodapé';
      default:
        return 'Corpo';
    }
  }

  getParametroControlName(key: string): string {
    return `parametro_${key.replace(/[^a-zA-Z0-9_]/g, '_')}`;
  }

  private initializeSendForm() {
    this.form = this.formBuilder.group({
      telefone: ['', Validators.required]
    });
  }

  private extractSendParametros(components: MetaMsgTemplateComponentModel[]): MetaMsgTemplateSendParametro[] {
    const parametros = new Map<string, MetaMsgTemplateSendParametro>();

    components
      .filter(component => component.tipo === MetaMsgTemplateComponentType.Header || component.tipo === MetaMsgTemplateComponentType.Body)
      .forEach(component => {
        const variables = this.extractVariables(component.text);

        variables.forEach(variable => {
          const parametro = (component.parametros || []).find(item => item.nome === variable || `${item.ordem}` === variable);

          if (!parametros.has(variable)) {
            parametros.set(variable, {
              key: variable,
              label: parametro?.nome || variable,
              exemplo: parametro?.exemplo || ''
            });
          }
        });
      });

    return Array.from(parametros.values()).sort((a, b) => {
      const aNumber = Number(a.key);
      const bNumber = Number(b.key);

      if (!Number.isNaN(aNumber) && !Number.isNaN(bNumber)) {
        return aNumber - bNumber;
      }

      return a.key.localeCompare(b.key);
    });
  }

  private extractVariables(text: string): string[] {
    if (!text) {
      return [];
    }

    const variables = new Set<string>();
    const regex = /\{\{([^}]+)\}\}/g;
    let match = regex.exec(text);

    while (match) {
      variables.add(match[1].trim());
      match = regex.exec(text);
    }

    return Array.from(variables);
  }
}
